import { gx, GTAX_HOST } from "@/components/gtax/base";
import { useEffect } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/components/gtax/gtax.css";
import { Nav, Footer } from "@/components/gtax/GtaxShell";
import { GtaxHero } from "@/components/gtax/GtaxHero";
import { GtaxPerformance } from "@/components/gtax/GtaxPerformance";
import { GtaxHow, GtaxRoadmap } from "@/components/gtax/GtaxHow";
import { GtaxTiers } from "@/components/gtax/GtaxTiers";
import { GtaxCalculator } from "@/components/gtax/GtaxCalculator";
import { GtaxProjection } from "@/components/gtax/GtaxProjection";
import { GtaxTraded } from "@/components/gtax/GtaxTraded";
import GtaxAbout from "@/components/gtax/pages/About";
import GtaxStrategy from "@/components/gtax/pages/Strategy";
import GtaxReport from "@/components/gtax/pages/Report";
import GtaxAccess from "@/components/gtax/pages/Access";
import GtaxLegal from "@/components/gtax/pages/Legal";
import GtaxContact from "@/components/gtax/pages/Contact";
import GtaxStatus from "@/components/gtax/pages/Status";
import GtaxAdmin from "@/components/gtax/pages/Admin";
import { PageHead } from "@/components/gtax/GtaxShell";

const Gallery = () => (
  <section className="py-24 gx-line" data-testid="gtax-gallery">
    <div className="gx-wrap grid md:grid-cols-3 gap-6">
      {[["bar.jpg", "999.9 fine gold"], ["chart.jpg", "XAU/USD, every session"], ["coins.jpg", "Measured, balanced risk"]].map(([src, cap]) => (
        <figure key={src} className="gx-photo h-80">
          <img src={`/gtax/${src}`} alt={cap} />
          <figcaption className="absolute bottom-0 inset-x-0 p-5 gx-caps text-sm text-white bg-gradient-to-t from-black/60 to-transparent">{cap}</figcaption>
        </figure>
      ))}
    </div>
  </section>
);

const Cta = () => (
  <section className="py-32 gx-line text-center relative overflow-hidden" data-testid="gtax-access">
    <img src="/gtax/vault.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-[.15]" />
    <div className="gx-wrap relative">
      <img src="/gtax/logo.png" alt="" className="w-32 h-32 mx-auto" />
      <h2 className="gx-serif text-5xl md:text-6xl mt-10">By invitation, for now.</h2>
      <p className="mt-6 text-[color:var(--mute)] max-w-xl mx-auto text-lg">GTA X opens first to a private circle of members. Sign the digital agreement, verify your identity and settle your allocation.</p>
      <Link to={gx("/access")} className="gx-btn mt-12" data-testid="gtax-access-cta">Request access</Link>
    </div>
  </section>
);

const Home = () => (
  <>
    <GtaxHero />
    <GtaxPerformance />
    <GtaxHow />
    <GtaxTraded />
    <GtaxTiers />
    <GtaxProjection />
    <GtaxCalculator />
    <Gallery />
    <GtaxRoadmap />
    <Cta />
  </>
);

const Membership = () => (
  <>
    <PageHead eyebrow="Membership" title="Three tiers. One mandate. A larger share as you grow." img="/gtax/bars.jpg" />
    <GtaxTiers />
    <GtaxProjection />
    <GtaxCalculator />
    <Cta />
  </>
);

export default function GtaxPage() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => {
    document.title = "GTA X — Gold Trading Association";
    if (GTAX_HOST) document.querySelectorAll("link[rel~='icon']").forEach((l) => { l.href = "/gtax/logo.png"; });
  }, []);
  return (
    <div className="gx t-champagne" id="top" data-testid="gtax-page">
      <Toaster position="top-center" />
      <Nav />
      <Routes>
        <Route index element={<Home />} />
        <Route path="about" element={<GtaxAbout />} />
        <Route path="strategy" element={<GtaxStrategy />} />
        <Route path="report" element={<GtaxReport />} />
        <Route path="membership" element={<Membership />} />
        <Route path="access" element={<GtaxAccess />} />
        <Route path="contact" element={<GtaxContact />} />
        <Route path="status" element={<GtaxStatus />} />
        <Route path="admin" element={<GtaxAdmin />} />
        {["disclaimer", "terms", "privacy", "risk"].map((d) => <Route key={d} path={d} element={<GtaxLegal doc={d} />} />)}
        <Route path="*" element={<Home />} />
      </Routes>
      <Footer />
    </div>
  );
}
